import {useMutation, UseMutationOptions, useQuery, UseQueryOptions} from '@tanstack/react-query';
import {useAtomValue, useSetAtom} from 'jotai';
import {errorAtom, teamsUserCredentialAtom} from '../store';

export type ApiError = Error & {
    errorCode?: string;
    retryAfterMs?: number;
    status?: number;
};

export function createApiErrorFromResponse(text: string, status: number, statusText?: string): ApiError {
    let parsed: { errorCode?: string; message?: string; retryAfterMs?: number } | undefined;
    try {
        parsed = text ? JSON.parse(text) as { errorCode?: string; message?: string; retryAfterMs?: number } : undefined;
    } catch {
        parsed = undefined;
    }

    const error = new Error(
        parsed?.message
        || parsed?.errorCode
        || (statusText ? `${statusText}: ${status}` : `Unknown error: ${status}`)
    ) as ApiError;
    error.status = status;
    error.errorCode = parsed?.errorCode;
    if (typeof parsed?.retryAfterMs === "number") {
        error.retryAfterMs = parsed.retryAfterMs;
    }
    return error;
}

export function parseApiResponseText<TData = unknown>(text: string, status = 200): TData {
    if (!text || text.trim().length === 0) {
        return null as TData;
    }

    try {
        return JSON.parse(text) as TData;
    } catch {
        const error = new Error(`Invalid JSON response: ${status}`) as ApiError;
        error.status = status;
        throw error;
    }
}

// For GET requests
type ApiQueryOptions<TData> = Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'> & {
    skipErrorNotification?: boolean;
};

export function useApiQuery<TData = unknown>(
    path: string,
    options?: ApiQueryOptions<TData>
) {
    const setError = useSetAtom(errorAtom);
    const teamsUserCredential = useAtomValue(teamsUserCredentialAtom);
    const {skipErrorNotification, ...queryOptions} = options ?? {};

    return useQuery<TData, Error>({
        queryKey: ['api', path],
        queryFn: async () => {
            if (!teamsUserCredential) {
                throw new Error('User credentials not available');
            }
            let accessToken;
            try {
                accessToken = await teamsUserCredential.getToken([]);
            } catch (tokenError: unknown) {
                const message = tokenError instanceof Error ? tokenError.message : String(tokenError);
                throw new Error(`Authentication error: ${message}`);
            }
            if (!accessToken) {
                throw new Error('Failed to acquire access token');
            }

            try {
                const response = await fetch(path, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken?.token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw createApiErrorFromResponse(await response.text(), response.status, response.statusText);
                }

                const text = await response.text();
                return parseApiResponseText<TData>(text, response.status);
            } catch (error) {
                if (!skipErrorNotification) {
                    setError(error instanceof Error ? error.message : 'An unknown error occurred');
                }
                throw error;
            }
        },
        ...queryOptions
    });
}

export function useApiMutation<TData = unknown, TVariables = unknown>(
    method: 'POST' | 'DELETE',
    options?: Omit<UseMutationOptions<TData, Error, { path: string; body?: TVariables }> & {
        skipErrorNotification?: boolean
    }, 'mutationFn'>
) {
    const setError = useSetAtom(errorAtom);
    const teamsUserCredential = useAtomValue(teamsUserCredentialAtom);

    return useMutation<TData, Error, { path: string; body?: TVariables }>({
        mutationFn: async ({path, body}) => {
            try {
                const accessToken = await teamsUserCredential?.getToken([]);

                const response = await fetch(path, {
                    method,
                    body: body ? JSON.stringify(body) : undefined,
                    headers: {
                        'Authorization': `Bearer ${accessToken?.token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw createApiErrorFromResponse(await response.text(), response.status, response.statusText);
                }

                const text = await response.text();
                return parseApiResponseText<TData>(text, response.status);
            } catch (error) {
                if (!options?.skipErrorNotification) {
                    setError(error instanceof Error ? error.message : 'An unknown error occurred');
                }
                throw error;
            }
        },
        ...options
    });
}
